import React, { useRef } from "react";
import { Provider } from "react-redux";
import type { Store } from "redux";
import { DesignSystemProvider, theme } from "@claroty/design-system";
import { useEvent } from "@claroty/utilities/react/tsHooks";
import { getHistory } from "@claroty/utilities/store/history";
import { AppContainerProvider } from "@claroty/xdome-design-system/providers/AppContainerProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { Router } from "react-router-dom";
import { APP_DIV } from "~/const/preAuthConsts";
import { initPostAuthStore as initPostAuthStoreHelper } from "~/store/initStore";
import { isBrowserSupported } from "~/utils/preAuthUtils";
import type ReduxStateType from "../../reducers/ReduxStateType";
import BrowserNotSupported from "../common/BrowserNotSupported/BrowserNotSupported";
import reactQueryClient from "../../api/reactQuery/reactQueryClient";
import BaseApp from "../BaseApp";
import TranslationProvider from "../app/TranslationProvider";
import { useRootInitialization } from "./useRootInitialization";

const StrictModeWrapper = ({ children }: { children: React.ReactNode }) =>
    process.env.NODE_ENV === "development" ? <React.StrictMode>{children}</React.StrictMode> : children;

export type RootProps = {
    store: Store<ReduxStateType>;
    actions: {
        setIsAuthenticated: (isAuthenticated: boolean) => void;
        restoreLogin: () => Promise<void>;
        startLoading: (currentlyLoading: string | boolean, contextElement?: HTMLElement) => void;
        endLoading: () => void;
        setDarkMode: (isDarkMode: boolean) => void;
    };
    isAuthenticated?: boolean;
};

const Root = ({ store, actions, isAuthenticated }: RootProps) => {
    const isPostAuthStoreInitialized = useRef(false);

    const handleLogin = useEvent(async () => {
        // if we are re-logging in after logging out we already have the full post auth store
        if (!isPostAuthStoreInitialized.current) {
            await initPostAuthStoreHelper(store);
            isPostAuthStoreInitialized.current = true;
        }

        actions.setIsAuthenticated(true);
    });

    useRootInitialization({ store, actions, isPostAuthStoreInitializedRef: isPostAuthStoreInitialized });

    if (!isBrowserSupported()) return <BrowserNotSupported />;

    return (
        <TranslationProvider isAuthenticated={isAuthenticated}>
            <Provider store={store}>
                <Router history={getHistory()}>
                    <StrictModeWrapper>
                        <QueryClientProvider client={reactQueryClient}>
                            <AppContainerProvider container={APP_DIV}>
                                <DesignSystemProvider theme={theme}>
                                    <BaseApp isAuthenticated={isAuthenticated} onLogin={handleLogin} />
                                </DesignSystemProvider>
                            </AppContainerProvider>
                        </QueryClientProvider>
                    </StrictModeWrapper>
                </Router>
            </Provider>
        </TranslationProvider>
    );
};

export default Root;
